import type { SingularKeys } from '@libs/i18n';
import type {
  AssessmentProgressDocument,
  AssessmentProgressSnapshot,
  GetAssessmentProgressResponse,
  StageProgress,
} from '@libs/types';

import {
  ASSESSMENT_PROGRESS_STALL_THRESHOLD_MS,
  ASSESSMENT_PROGRESS_STARTUP_GRACE_MS,
  ASSESSMENT_STAGE_KEYS,
  ASSESSMENT_STAGE_WEIGHTS,
  type AssessmentDocumentProgressState,
  type AssessmentStageKey,
  AssessmentStatus,
} from '@libs/constants';

/**
 * Everything the run screen derives rather than receives.
 *
 * Two of these could not be sent even in principle. "Stalled" is silence, and a
 * run that has gone quiet cannot report that it has gone quiet. "No data" is the
 * absence of every report, so there is no report to carry it. Both are computed
 * here from `lastEventAt` against the SERVER's clock, which the endpoint returns
 * for exactly this reason — a browser an hour fast would otherwise invent a
 * stall on a perfectly healthy run.
 */

// =============================================================================
// Run state
// =============================================================================

/**
 * Which of the design's states the screen is in.
 *
 * `unknown` and `stalled` both mean "we cannot see what is happening" and are
 * deliberately distinct from `queued` and `starting`, which mean "nothing has
 * happened yet". Rendering silence as 0% is the specific dishonesty this screen
 * replaced — but so is rendering start-up as a blind spot, which is what the
 * screen did before `starting` existed: pressing submit put you in front of
 * "this run is not reporting progress" while the Job was still being scheduled.
 */
export type RunView =
  | 'queued'
  | 'starting'
  | 'early'
  | 'grading'
  | 'done'
  | 'runFailed'
  | 'stalled'
  | 'unknown';

/**
 * A run that can still change, and therefore still worth watching.
 *
 * The single answer to a question three parts of the screen ask independently:
 * the poller decides when to stop fetching, the page decides when to stop
 * ticking the elapsed clock, and `deriveRunView` decides which copy to draw. As
 * three separate spellings they had to be kept in agreement by hand, and adding
 * or retiring an `AssessmentStatus` while updating two of them gives you a
 * screen that polls a settled run forever, or an elapsed pill frozen on a live
 * one.
 *
 * Membership, not `!terminal`: DRAFT is not terminal and is also not a run.
 */
const ACTIVE_STATUSES: readonly AssessmentStatus[] = [
  AssessmentStatus.SUBMITTED,
  AssessmentStatus.PROCESSING,
];

export function isRunActive(status: AssessmentStatus): boolean {
  return ACTIVE_STATUSES.includes(status);
}

export function deriveRunView(
  data: Pick<GetAssessmentProgressResponse, 'status' | 'reporting' | 'snapshot'> &
    Partial<Pick<GetAssessmentProgressResponse, 'startedAt'>>,
  serverNow: number,
): RunView {
  if (data.status === AssessmentStatus.COMPLETED) return 'done';
  if (data.status === AssessmentStatus.FAILED) return 'runFailed';

  // Never reported, which is three different situations wearing the same
  // absence of data — and they get three different answers.
  if (!data.reporting || !data.snapshot) {
    // Not picked up yet. Nothing has happened, which is a fact rather than a
    // blind spot.
    if (data.status === AssessmentStatus.SUBMITTED) return 'queued';

    // Dispatched, but inside the window where a healthy run has not had time to
    // say anything: the status flips when the Job is handed off, and the Job
    // still has to be scheduled, pull its image and cold-start before intake
    // emits its first event. Calling that "not reporting" is both alarming and
    // usually wrong, and it is what every user saw the moment they submitted.
    if (isWithinStartupGrace(data.startedAt, serverNow)) return 'starting';

    // Past the window. A run predating this feature, or one whose Job could not
    // reach the callback URL, looks exactly like this and must not be drawn as
    // a bar sitting at zero.
    return 'unknown';
  }

  if (isStalled(data.snapshot, serverNow)) return 'stalled';

  const stage = data.snapshot.currentStageKey;
  if (stage === 'grading' || stage === 'scoring' || stage === 'reporting') return 'grading';
  return 'early';
}

/**
 * Was reporting, then stopped.
 *
 * Only meaningful for a run still believed to be active — a completed run's
 * last event is always old, and calling that stalled would put a warning on
 * every finished assessment ever opened.
 */
export function isStalled(snapshot: AssessmentProgressSnapshot, serverNow: number): boolean {
  const last = new Date(snapshot.lastEventAt).getTime();
  if (Number.isNaN(last)) return false;
  return serverNow - last > ASSESSMENT_PROGRESS_STALL_THRESHOLD_MS;
}

/**
 * Young enough that reporting nothing is still the expected state.
 *
 * A missing or unparseable timestamp reads as OUT of grace, so the screen falls
 * back to admitting it cannot see the run. The alternative — treating "we do not
 * know when this started" as "it must have just started" — would leave a run
 * that died months ago claiming to be warming up.
 */
export function isWithinStartupGrace(startedAt: string | null | undefined, now: number): boolean {
  if (!startedAt) return false;
  const started = new Date(startedAt).getTime();
  if (Number.isNaN(started)) return false;
  return now - started < ASSESSMENT_PROGRESS_STARTUP_GRACE_MS;
}

/** Whole minutes since the run's first reported event. */
export function minutesSince(iso: string | null | undefined, now: number): number {
  if (!iso) return 0;
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return 0;
  return Math.max(0, Math.floor((now - then) / 60_000));
}

/** `mm:ss`-style elapsed, matching the timeline's own column. */
export function formatElapsed(totalMinutes: number): string {
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
}

/**
 * Offset of one event from the run's start, as `mm:ss` — the timeline's left
 * column. Relative rather than wall-clock because the question a reader has is
 * "how far into the run did this happen", not "what time was it".
 */
export function timelineOffset(occurredAt: string, firstEventAt: string | undefined): string {
  const at = new Date(occurredAt).getTime();
  const start = firstEventAt ? new Date(firstEventAt).getTime() : NaN;
  if (Number.isNaN(at) || Number.isNaN(start)) return '';
  const seconds = Math.max(0, Math.floor((at - start) / 1000));
  const mm = Math.floor(seconds / 60);
  const ss = seconds % 60;
  return `${String(mm).padStart(2, '0')}:${String(ss).padStart(2, '0')}`;
}

// =============================================================================
// Stage bar
// =============================================================================

/**
 * One label per stage, in the order the run performs them.
 *
 * Typed against `AssessmentStageKey` rather than `string`, and held here rather
 * than in each of the three components that render a stage name: a sixth stage
 * added to `ASSESSMENT_STAGE_KEYS` must break the build in exactly one place.
 * As three loose `Record<string, …>` copies it broke in none of them — the bar
 * kept working while the hero rendered a blank label and the timeline silently
 * dropped every event for the new stage.
 */
export const STAGE_LABEL_KEYS: Record<AssessmentStageKey, SingularKeys> = {
  intake: 'app.assessment.progress.stage.intake',
  classification: 'app.assessment.progress.stage.classification',
  grading: 'app.assessment.progress.stage.grading',
  scoring: 'app.assessment.progress.stage.scoring',
  reporting: 'app.assessment.progress.stage.reporting',
};

export interface StageSegment {
  key: AssessmentStageKey;
  /** Relative width. NOT a share of a total — see the note below. */
  weight: number;
  /** 0-100, counted work. */
  done: number;
  /** 0-100, known work in flight, drawn as a hatched buffer behind `done`. */
  inFlight: number;
  isCurrent: boolean;
  counted: number | null;
  inFlightCount: number | null;
  total: number | null;
}

/**
 * The five segments, each filled by its own measured fraction.
 *
 * THE WEIGHTS ARE WIDTHS. No composite percentage is computed anywhere, because
 * there is nothing honest to compute: the segments measure different units —
 * documents read, documents assessed, a binary done/not-done — and averaging
 * them would produce exactly the invented number this screen exists to remove.
 */
export function buildStageSegments(snapshot: AssessmentProgressSnapshot | null): StageSegment[] {
  return ASSESSMENT_STAGE_KEYS.map((key) => {
    const stage: StageProgress | undefined = snapshot?.stages?.[key];
    return {
      key,
      weight: ASSESSMENT_STAGE_WEIGHTS[key],
      done: toPercent(stage?.done),
      inFlight: toPercent(stage?.inFlight),
      isCurrent: snapshot?.currentStageKey === key,
      counted: stage?.counted ?? null,
      inFlightCount: stage?.inFlightCount ?? null,
      total: stage?.total ?? null,
    };
  });
}

function toPercent(fraction: number | undefined): number {
  if (typeof fraction !== 'number' || Number.isNaN(fraction)) return 0;
  return Math.max(0, Math.min(100, Math.round(fraction * 100)));
}

// =============================================================================
// Document rows
// =============================================================================

/**
 * A row's state, which is the reported state plus the two the client derives.
 *
 * `queued` is what a document with no reported state renders as while the run
 * is live; `nodata` is what it renders as when the run itself is not reporting.
 * The distinction matters — one says the document is waiting its turn, the
 * other says we cannot see the document at all.
 */
export type DocumentRowState = AssessmentDocumentProgressState | 'queued' | 'nodata' | 'stalled';

export function deriveDocumentRowState(
  doc: AssessmentProgressDocument,
  runView: RunView,
): DocumentRowState {
  if (runView === 'unknown') return 'nodata';
  if (!doc.state) return 'queued';
  // A document mid-grade on a run that has gone silent is not being graded any
  // more; it is the last thing we saw. Settled states are left alone — a
  // finished document stays finished however quiet the run went afterwards.
  if (runView === 'stalled' && (doc.state === 'grading' || doc.state === 'reading')) {
    return 'stalled';
  }
  return doc.state;
}

/**
 * Unmet requirements for one document.
 *
 * Gaps, not a score. `questionsMet / questionsTotal` is a ratio that reads like
 * maturity and is not maturity — maturity is weighted, spans domains, and
 * counts documents that were never supplied. A plain count of what is missing
 * cannot be mistaken for it.
 *
 * Returns null before the document is fully graded: mid-document the figures
 * genuinely move, because the AI's per-document score divides by the sections
 * that exist AT THAT MOMENT, so a document at 6 of 9 sections carries a real,
 * plausible, wrong number.
 */
export function documentGaps(doc: AssessmentProgressDocument): number | null {
  if (doc.state !== 'graded') return null;
  if (doc.questionsTotal == null || doc.questionsMet == null) return null;
  return Math.max(0, doc.questionsTotal - doc.questionsMet);
}

/**
 * How far the grader has got through one document, as a percentage.
 *
 * Sections are the unit the grader reports in, and they are not a unit anyone
 * outside the pipeline can size. “4 of 12 sections” invites a reader to compare
 * one document's twelfths against another document's thirds as though the two
 * were the same amount of work, and to add them up across a table where they do
 * not add up. The fraction is the only half of that pair that means anything on
 * its own, so it is the only half that leaves this module.
 *
 * Null when there is nothing to divide by — a document the grader has not
 * reached yet, or one still being read. Floored rather than rounded, and held
 * at 99 until the last section actually lands, because a 200-section document
 * at 199 rounds to 100% and a row that says 100% while the spinner is still
 * turning is the one number on this screen a reader would call a lie.
 */
export function gradingPercent(doc: AssessmentProgressDocument): number | null {
  const total = doc.sectionsTotal ?? 0;
  if (total <= 0) return null;

  const done = Math.max(0, doc.sectionsGraded ?? 0);
  if (done >= total) return 100;

  return Math.min(99, Math.floor((done / total) * 100));
}

export interface DocumentGroup {
  domainKey: string;
  domainName: string;
  documents: AssessmentProgressDocument[];
}

/**
 * Documents grouped by domain, preserving the order the API returned.
 *
 * The API orders by `domains.key` because `domains.name` is a jsonb blob whose
 * sort order would be by serialized JSON. Insertion order here inherits that,
 * so the grouping is stable across polls even as rows change state.
 */
export function groupDocumentsByDomain(documents: AssessmentProgressDocument[]): DocumentGroup[] {
  const groups = new Map<string, DocumentGroup>();

  for (const doc of documents) {
    const key = doc.domainKey || '';
    const existing = groups.get(key);
    if (existing) {
      existing.documents.push(doc);
    } else {
      groups.set(key, { domainKey: key, domainName: doc.domainName, documents: [doc] });
    }
  }

  return [...groups.values()];
}

/**
 * Counters for the summary row.
 *
 * Taken from the snapshot when there is one — it is what the recorder actually
 * counted — and folded from the document rows otherwise, so a run whose
 * snapshot has not caught up still shows something true.
 *
 * `notSupplied` is the exception and can only come from the snapshot: an
 * expected-but-absent document has no row here to count. It no longer appears in
 * the counter row — every cell there is a statement about the run, and a
 * document the run will never touch was not one — but the completion copy still
 * needs it, because it scores zero in the final maturity calculation and the
 * report will therefore read lower than the per-document figures on screen.
 */
export function resolveCounters(
  snapshot: AssessmentProgressSnapshot | null,
  documents: AssessmentProgressDocument[],
  runView: RunView,
  /**
   * Whether `documents` is every document or one page.
   *
   * Load-bearing: the fallback below folds the rows it is given, and folding a
   * PAGE would put "3 assessed" above a run that has assessed thirty. The
   * counters are whole-run statements, so when only a page is held there is
   * nothing honest to fall back to and every count reads zero instead.
   */
  documentsArePartial = false,
  /**
   * Documents in scope for the whole run, where the caller knows it.
   *
   * Used for exactly one thing: a run with no snapshot has reported nothing, so
   * every document in scope is queued by definition. That is a whole-run fact a
   * single page of rows can still state, because it does not come from the rows
   * — without it the counter row reads "Queued 0" directly above a table of
   * twenty queued documents.
   */
  documentsTotal?: number,
): Record<
  'queued' | 'reading' | 'read' | 'grading' | 'assessed' | 'failed' | 'notSupplied',
  number
> {
  if (snapshot) {
    return {
      queued: snapshot.counters.queued,
      reading: snapshot.counters.reading,
      read: snapshot.counters.read,
      grading: snapshot.counters.grading,
      assessed: snapshot.counters.assessed,
      failed: snapshot.counters.failed,
      notSupplied: snapshot.counters.notSupplied,
    };
  }

  const counts = {
    queued: 0,
    reading: 0,
    read: 0,
    grading: 0,
    assessed: 0,
    failed: 0,
    notSupplied: 0,
  };

  if (documentsArePartial) {
    if (typeof documentsTotal === 'number') counts.queued = documentsTotal;
    return counts;
  }

  for (const doc of documents) {
    const state = deriveDocumentRowState(doc, runView);
    if (state === 'graded') counts.assessed += 1;
    else if (state === 'failed') counts.failed += 1;
    else if (state === 'grading' || state === 'stalled') counts.grading += 1;
    else if (state === 'reading') counts.reading += 1;
    else if (state === 'read' || state === 'classified') counts.read += 1;
    else counts.queued += 1;
  }

  return counts;
}
